import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Edit, Trash2, Eye, Star } from 'lucide-react';
import { toast } from 'react-hot-toast';
import apiService from '../../services/api';
import DataTable from '../common/DataTable';
import Modal from '../common/Modal';

const Features = ({ user }) => {
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingFeature, setEditingFeature] = useState(null);
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    icon: '',
    order_index: 0,
    image: null
  });

  useEffect(() => {
    fetchFeatures();
  }, []);

  const fetchFeatures = async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        apiService.setToken(token);
      }
      const response = await apiService.getFeatures();
      setFeatures(Array.isArray(response) ? response : response.data || []);
    } catch (error) {
      console.error('Fetch features error:', error);
      toast.error('Failed to fetch features');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      if (token) {
        apiService.setToken(token);
      }

      const data = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== '') {
          data.append(key, formData[key]);
        }
      });

      let response;
      if (editingFeature) {
        response = await apiService.updateFeature(editingFeature.id, data);
      } else {
        response = await apiService.createFeature(data);
      }

      toast.success(`Feature ${editingFeature ? 'updated' : 'created'} successfully`);
      setShowModal(false);
      setEditingFeature(null);
      setFormData({ title: '', description: '', icon: '', order_index: 0, image: null });
      fetchFeatures();
    } catch (error) {
      console.error('Feature operation error:', error);
      toast.error(error.message || 'Operation failed');
    }
  };

  const handleView = (feature) => {
    setSelectedFeature(feature);
    setShowViewModal(true);
  };

  const handleEdit = (feature) => {
    setEditingFeature(feature);
    setFormData({
      title: feature.title,
      description: feature.description,
      icon: feature.icon,
      order_index: feature.order_index,
      image: null
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    toast((t) => (
      <div>
        <p>Are you sure you want to delete this feature?</p>
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
          <button
            onClick={async () => {
              toast.dismiss(t.id);
              try {
                const token = localStorage.getItem('token');
                if (token) {
                  apiService.setToken(token);
                }
                await apiService.deleteFeature(id);
                toast.success('Feature deleted successfully');
                fetchFeatures();
              } catch (error) {
                toast.error('Failed to delete feature');
              }
            }}
            style={{ background: '#ef4444', color: 'white', border: 'none', padding: '0.25rem 0.5rem', borderRadius: '4px' }}
          >
            Delete
          </button>
          <button
            onClick={() => toast.dismiss(t.id)}
            style={{ background: '#6b7280', color: 'white', border: 'none', padding: '0.25rem 0.5rem', borderRadius: '4px' }}
          >
            Cancel
          </button>
        </div>
      </div>
    ), { duration: Infinity });
  };

  const columns = [
    { key: 'order_index', label: 'Order' },
    { key: 'title', label: 'Title' },
    {
      key: 'description',
      label: 'Description',
      render: (feature) => (
        feature.description?.length > 50
          ? `${feature.description.substring(0, 50)}...`
          : feature.description || 'N/A'
      )
    },
    {
      key: 'icon',
      label: 'Icon',
      render: (feature) => (
        feature.icon ? <i className={feature.icon}></i> : 'N/A'
      )
    },
    { key: 'Creator.name', label: 'Created By', render: (feature) => feature.Creator?.name || 'N/A' },
    {
      key: 'actions',
      label: 'Actions',
      render: (feature) => (
        <div className="action-buttons">
          <button onClick={() => handleView(feature)} className="btn-sm" title="View">
            <Eye size={16} />
          </button>
          <button onClick={() => handleEdit(feature)} className="btn-edit" title="Edit">
            <Edit size={16} />
          </button>
          {user?.role === 'super_admin' && (
            <button onClick={() => handleDelete(feature.id)} className="btn-delete" title="Delete">
              <Trash2 size={16} />
            </button>
          )}
        </div>
      )
    }
  ];

  return (
    <div className="features-management">
      <div className="page-header">
        <h2><Star size={24} /> Features Management</h2>
        <motion.button
          className="btn-primary"
          onClick={() => setShowModal(true)}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <Plus size={16} /> Add Feature
        </motion.button>
      </div>

      <DataTable
        data={features}
        columns={columns}
        loading={loading}
        emptyMessage="No features found"
      />

      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingFeature(null);
          setFormData({ title: '', description: '', icon: '', order_index: 0, image: null });
        }}
        title={`${editingFeature ? 'Edit' : 'Add'} Feature`}
      >
        <form onSubmit={handleSubmit} style={{ textAlign: 'left' }}>
          <div className="form-group">
            <label>Title</label>
            <input
              type="text"
              required
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea
              required
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows="4"
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Icon</label>
              <input
                type="text"
                placeholder="e.g., fa-star, material-icon-name"
                value={formData.icon}
                onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Order Index</label>
              <input
                type="number"
                value={formData.order_index}
                onChange={(e) => setFormData({ ...formData, order_index: parseInt(e.target.value) })}
              />
            </div>
          </div>
          <div className="form-group">
            <label>Image</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setFormData({ ...formData, image: e.target.files[0] })}
            />
          </div>

          <div className="modal-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => {
                setShowModal(false);
                setEditingFeature(null);
                setFormData({ title: '', description: '', icon: '', order_index: 0, image: null });
              }}
            >
              Cancel
            </button>
            <button type="submit" className="btn-primary">
              {editingFeature ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </Modal>

      {/* View Modal */}
      <Modal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        title="Feature Details"
        size="lg"
      >
        {selectedFeature && (
          <div style={{ textAlign: 'left', padding: '0' }}>
            {/* Header Section */}
            <div style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              padding: '2rem',
              borderRadius: '12px 12px 0 0',
              marginBottom: '1.5rem',
              color: 'white',
              textAlign: 'center'
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1rem',
                fontSize: '2rem',
                fontWeight: 'bold'
              }}>
                <Star size={32} />
              </div>
              <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.5rem' }}>{selectedFeature.title}</h3>
              <p style={{ margin: '0', opacity: '0.9' }}>Feature #{selectedFeature.order_index}</p>
            </div>

            {/* Content Info */}
            <div style={{ marginBottom: '1.5rem' }}>
              <h4 style={{ margin: '0 0 1rem', color: '#374151', fontSize: '1.1rem' }}>Description</h4>
              <div style={{ padding: '0.75rem', background: '#f9fafb', borderRadius: '8px' }}>
                <p style={{ margin: '0', color: '#6b7280', whiteSpace: 'pre-wrap' }}>{selectedFeature.description}</p>
              </div>
            </div>

            {/* Meta Information */}
            <div style={{ marginBottom: '1.5rem' }}>
              <h4 style={{ margin: '0 0 1rem', color: '#374151', fontSize: '1.1rem' }}>Details</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div style={{ padding: '1rem', background: '#f3f4f6', borderRadius: '8px' }}>
                  <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>Icon</div>
                  <div style={{ fontWeight: '500', color: '#374151' }}>
                    {selectedFeature.icon ? (
                      <span><i className={selectedFeature.icon}></i> {selectedFeature.icon}</span>
                    ) : 'N/A'}
                  </div>
                </div>
                <div style={{ padding: '1rem', background: '#f3f4f6', borderRadius: '8px' }}>
                  <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>Created By</div>
                  <div style={{ fontWeight: '500', color: '#374151' }}>{selectedFeature.Creator?.name || 'N/A'}</div>
                </div>
              </div>
            </div>

            {/* Status Badge */}
            <div style={{ textAlign: 'center' }}>
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 1rem',
                background: selectedFeature.is_active ? '#dcfce7' : '#f3f4f6',
                color: selectedFeature.is_active ? '#166534' : '#374151',
                borderRadius: '20px',
                fontSize: '0.875rem',
                fontWeight: '500'
              }}>
                <div style={{
                  width: '8px',
                  height: '8px',
                  background: selectedFeature.is_active ? '#22c55e' : '#6b7280',
                  borderRadius: '50%'
                }} />
                {selectedFeature.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Features;